import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable} from "inversify";
import {BeforeTrainingProgressEvent} from "@/modules/training/events/BeforeTrainingProgressEvent";
import {EffectTypeEnum} from "@/modules/club-grounds/enums";
import {BeforeYouthPlayerScoutingEvent} from "@/modules/youth/event/BeforeYouthPlayerScoutingEvent";
import {BeforeNegotiationEvent} from "@/modules/diplomacy/events/BeforeNegotiationEvent";
import AggregatedEffectProvider from "@/modules/club-grounds/services/AggregatedEffectProvider";
import {AfterSponsoringOffersCreationEvent} from "@/modules/sponsoring/events/AfterSponsoringOffersCreationEvent";
import {Sponsor} from "@/modules/sponsoring/entity";
import {BeforeGroundSlotDemolishedEvent} from "@/modules/club-grounds/events/BeforeGroundSlotDemolishedEvent";
import {AfterGroundSlotFinishedEvent} from "@/modules/club-grounds/events/AfterGroundSlotFinishedEvent";
import {Team} from "@/modules/team/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";

@injectable()
export class EffectsEventSubscriber implements EventSubscriber {
  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(AggregatedEffectProvider) private aggregatedEffectProvider: AggregatedEffectProvider,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: BeforeTrainingProgressEvent, callback: this.onBeforeTrainingProgressEvent},
      {event: BeforeYouthPlayerScoutingEvent, callback: this.onBeforeYouthPlayerScoutingEvent},
      {event: BeforeNegotiationEvent, callback: this.onBeforeNegotiationEvent},
      {event: AfterSponsoringOffersCreationEvent, callback: this.onAfterSponsoringOffersCreationEvent},
      {event: BeforeGroundSlotDemolishedEvent, callback: this.onBeforeGroundSlotDemolishedEvent},
      {event: AfterGroundSlotFinishedEvent, callback: this.onAfterGroundSlotFinishedEvent},
    ];
  }

  async onBeforeGroundSlotDemolishedEvent(event: BeforeGroundSlotDemolishedEvent): Promise<void> {
    const value = this.aggregatedEffectProvider.getSingleValue(EffectTypeEnum.STAFF_MEMBER_SLOTS, event.groundSlot);

    if (value !== null) {
      const team = await Team.current();
      team.staffSlots -= value;
      await team.save();
    }
  }

  async onAfterGroundSlotFinishedEvent(event: AfterGroundSlotFinishedEvent): Promise<void> {
    const value = this.aggregatedEffectProvider.getSingleValue(EffectTypeEnum.STAFF_MEMBER_SLOTS, event.groundSlot);

    if (value !== null) {
      const team = await Team.current();
      team.staffSlots += value;
      await team.save();
    }
  }

  async onAfterSponsoringOffersCreationEvent(event: AfterSponsoringOffersCreationEvent): Promise<void> {
    const sponsoringBonus = await this.aggregatedEffectProvider.getValue(EffectTypeEnum.SPONSORING_BASE_OFFER);

    if (sponsoringBonus) {
      await event.offers.forEachAsync(async (sponsor: Sponsor) => {
        sponsor.once *= 1 + sponsoringBonus / 100;
        sponsor.perCupMatch *= 1 + sponsoringBonus / 100;
        sponsor.perCupVictory *= 1 + sponsoringBonus / 100;
        sponsor.perLeagueMatch *= 1 + sponsoringBonus / 100;
        sponsor.perLeagueVictory *= 1 + sponsoringBonus / 100;

        await sponsor.save();

        console.debug('Added sponsoring bonus to offers.', {sponsor, sponsoringBonus});
      });
    }
  }

  async onBeforeNegotiationEvent(event: BeforeNegotiationEvent): Promise<void> {
    const negotiationProgress = await this.aggregatedEffectProvider.getValue(EffectTypeEnum.NEGOTIATION_PROGRESS);

    if (!negotiationProgress) {
      return;
    }

    const progress = event.negotiation.progress * negotiationProgress / 100;
    event.negotiation.progress += progress;
    await event.negotiation.save();

    console.debug('added negotiation progress by ground slot', {negotiation: event.negotiation, progress});
  }

  async onBeforeYouthPlayerScoutingEvent(event: BeforeYouthPlayerScoutingEvent): Promise<void> {
    const scoutingProgress = await this.aggregatedEffectProvider.getValue(EffectTypeEnum.SCOUTING_PROGRESS);

    if (!scoutingProgress) {
      return;
    }

    const revelation = event.progress * scoutingProgress / 100;
    event.player.revelation += revelation;
    await event.player.save();

    console.debug('added scouting progress by ground slot', {player: event.player, revelation});
  }

  async onBeforeTrainingProgressEvent(event: BeforeTrainingProgressEvent): Promise<void> {
    const trainingProgress = await this.aggregatedEffectProvider.getValue(EffectTypeEnum.TRAINING_PROGRESS);

    if (trainingProgress) {
      const progress = event.totalProgress * trainingProgress / 100;
      event.player.skill += progress;
      await event.player.save();

      console.debug('added training progress by ground slot', {player: event.player, progress});
    }

    const youthTrainingProgress = await this.aggregatedEffectProvider.getValue(EffectTypeEnum.YOUTH_TRAINING_PROGRESS);

    if (event.player.age <= this.configRepository.get('clubGrounds.effects.youth.ageLimit') && youthTrainingProgress) {
      const progress = event.totalProgress * youthTrainingProgress / 100;
      event.player.skill += progress;
      await event.player.save();

      console.debug('added youth training progress by ground slot', {player: event.player, progress});
    }
  }
}